import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCalendarSync } from '../components/calendar-sync/utils/useCalendarSync';
import { useStorefrontAuth } from '../../../auth/context/StorefrontAuthContext';
import { getPublicClientOrNull } from '../../../../api/companyRecords';
import { listMyStorefrontAppointments } from '../../../../api/storefront/client';
import '../styles/calendar-sync.css';

const SyncOverlay = ({ stage, progress }) => {
  const getStageConfig = () => {
    switch (stage) {
      case 'fetching':
        return {
          icon: (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
            </svg>
          ),
          title: 'Fetching Your Appointments',
          description: 'Please wait while we gather your appointment information...',
          showProgress: false
        };
      case 'syncing':
        return {
          icon: (
            <svg viewBox="0 0 24 24" fill="currentColor" className="syncing">
              <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
            </svg>
          ),
          title: 'Syncing with Calendar',
          description: progress ? `Syncing appointment ${progress.synced} of ${progress.total}` : 'Syncing your appointments...',
          showProgress: true
        };
      case 'completed':
        return {
          icon: (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
            </svg>
          ),
          title: 'Sync Complete',
          description: 'All your appointments have been successfully synced!',
          showProgress: false
        };
      default:
        return null;
    }
  };

  const config = getStageConfig();
  if (!config) return null;

  const progressValue = progress?.total > 0 
    ? (progress.synced / progress.total) * 100 
    : 0;

  return (
    <div className="sync-overlay">
      <div className="sync-overlay-content">
        <div className="sync-overlay-icon">
          {config.icon}
        </div>
        
        <h2 className="sync-overlay-title">
          {config.title}
        </h2>
        
        <p className="sync-overlay-description">
          {config.description}
        </p>

        {config.showProgress && (
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progressValue}%` }}
              />
            </div>
            <p className="progress-text">
              {Math.round(progressValue)}% Complete
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const CalendarSync = () => {
  const navigate = useNavigate();
  const { storefrontClient, loading: authLoading } = useStorefrontAuth();
  const clientUser = storefrontClient;
  const [error, setError] = React.useState(null);
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [syncProgress, setSyncProgress] = React.useState(null);
  const latestSyncStatus = React.useRef(null);
  const [syncStage, setSyncStage] = React.useState(null);

  const { companyUrl } = useParams();

  const { 
    syncStatus, 
    connectCalendar, 
    disconnectCalendar, 
    syncAppointment,
    setSyncStatus
  } = useCalendarSync(
    clientUser?.id,
    [] // We'll fetch appointments when needed
  );

  // Keep track of latest sync status
  React.useEffect(() => {
    latestSyncStatus.current = syncStatus;
  }, [syncStatus]);

  console.log('Client User Info:', {
    userId: clientUser?.id,
    portalUrl: clientUser?.portalUrl,
    companyUrl,
    companyId: clientUser?.companyId
  });

  const syncExistingAppointments = async (provider) => {
    try {
      console.log('Starting syncExistingAppointments:', {
        provider,
        userId: clientUser?.id,
        companyId: clientUser?.companyId
      });

      setIsSyncing(true);
      setSyncStage('fetching');
      setSyncProgress({ total: 0, synced: 0 });

      const appointments = await listMyStorefrontAppointments({
        companyId: clientUser.companyId,
        archived: false,
      });
      const terminalStatuses = new Set(['cancelled', 'archived', 'completed', 'no show']);
      const allAppointments = appointments.filter(
        (appointment) => !terminalStatuses.has(String(appointment.status || '').toLowerCase())
      );

      console.log('Total appointments to sync:', {
        total: allAppointments.length
      });

      setSyncStage('syncing');
      setSyncProgress({ total: allAppointments.length, synced: 0 });

      // Sync each appointment
      for (const appointment of allAppointments) {
        try {
          console.log('Syncing appointment:', {
            id: appointment.id,
            status: appointment.status,
            dateTime: appointment.dateTime
          });

          const result = await syncAppointment(appointment, provider);
          
          console.log('Sync result:', {
            appointmentId: appointment.id,
            result
          });

          setSyncProgress(prev => ({ ...prev, synced: prev.synced + 1 }));
        } catch (error) {
          console.error('Error syncing appointment:', {
            appointmentId: appointment.id,
            error: error.message,
            errorDetails: error
          });
        }
      }

      setSyncStage('completed');
      console.log('Finished syncing all appointments');
      return { success: true };
    } catch (error) {
      console.error('Error in syncExistingAppointments:', error);
      setSyncStage('error');
      throw error;
    } finally {
      setTimeout(() => {
        setIsSyncing(false);
        setSyncStage(null);
        setSyncProgress(null);
      }, 3000);
    }
  };

  const waitForConnection = async (provider) => {
    let attempts = 0;
    const maxAttempts = 10;
    
    while (attempts < maxAttempts) {
      console.log('Checking connection status:', {
        attempt: attempts + 1,
        status: latestSyncStatus.current?.[provider],
        connected: latestSyncStatus.current?.[provider]?.connected,
        error: latestSyncStatus.current?.[provider]?.error,
        calendarIntegrations: latestSyncStatus.current?.calendarIntegrations,
        timestamp: new Date().toISOString()
      });

      const clientData = await getPublicClientOrNull({
        companyId: clientUser.companyId,
        id: clientUser.id,
      });
      
      console.log('Client calendar check:', {
        attempt: attempts + 1,
        connected: !!clientData?.calendarIntegrations?.[provider],
        calendarIntegrations: clientData?.calendarIntegrations,
        timestamp: new Date().toISOString()
      });

      if (clientData?.calendarIntegrations?.[provider] && !latestSyncStatus.current?.[provider]?.connected) {
        console.log('Forcing state update from client record');
        
        // Update state with a Promise to ensure it completes
        await new Promise((resolve) => {
          setSyncStatus(prev => {
            const newState = {
              ...prev,
              [provider]: { 
                ...prev[provider], 
                connected: true,
                error: null
              }
            };
            console.log('State update:', {
              previous: prev,
              new: newState,
              timestamp: new Date().toISOString()
            });
            resolve();
            return newState;
          });
        });

        // Give React time to process the state update
        await new Promise(resolve => setTimeout(resolve, 500));

        // Check if update was successful using the ref
        if (latestSyncStatus.current?.[provider]?.connected) {
          console.log('State update confirmed successful');
          return true;
        }
      }

      if (latestSyncStatus.current?.[provider]?.connected) {
        return true;
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
    }

    return false;
  };

  const handleSync = async (provider) => {
    try {
      console.log('Starting calendar sync process:', {
        provider,
        userId: clientUser?.id,
        companyUrl,
        initialSyncStatus: latestSyncStatus.current[provider],
        timestamp: new Date().toISOString()
      });

      if (!clientUser?.id || !companyUrl) {
        throw new Error('Please wait while we load your account information');
      }

      console.log('Connecting calendar...');
      const result = await connectCalendar(provider);
      
      console.log('Calendar connection result:', {
        provider,
        result,
        currentSyncStatus: latestSyncStatus.current[provider],
        timestamp: new Date().toISOString()
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      console.log('Waiting for calendar connection to be established...');
      const connected = await waitForConnection(provider);

      if (!connected) {
        throw new Error('Failed to establish calendar connection. Please try again.');
      }

      console.log('Calendar connected successfully, starting appointment sync...');
      await syncExistingAppointments(provider);
      
      console.log('Calendar sync process completed');
    } catch (error) {
      console.error('Calendar sync process failed:', {
        provider,
        error: error.message,
        errorDetails: error,
        finalSyncStatus: latestSyncStatus.current[provider],
        timestamp: new Date().toISOString()
      });
      setError(error.message);
    }
  };

  const handleDisconnect = async (provider) => {
    try {
      if (!clientUser?.id || !companyUrl) {
        throw new Error('Please wait while we load your account information');
      }

      const result = await disconnectCalendar(provider);
      if (!result.success) {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error(`Failed to disconnect ${provider} calendar:`, error);
      setError(error.message);
    }
  };

  const calendarProviders = [
    {
      name: 'Google Calendar',
      provider: 'google',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      ),
      color: '#DB4437',
      colorDark: '#B31412',
      description: 'Sync with your Google Calendar account'
    },
    {
      name: 'Outlook Calendar',
      provider: 'outlook',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M21.59 11.59L13.5 3.5 12 2l-1.5 1.5-8.09 8.09L2 12l1.5 1.5 8.09 8.09L12 22l1.5-1.5 8.09-8.09L22 12l-1.5-1.5zM12 20l-1.5-1.5-8.09-8.09L2 12l1.5-1.5 8.09-8.09L12 2l1.5 1.5 8.09 8.09L22 12l-1.5 1.5-8.09 8.09L12 20z"/>
          <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
        </svg>
      ),
      color: '#0078D4',
      colorDark: '#005A9E',
      description: 'Sync with your Microsoft Outlook Calendar'
    },
  ];

  const getSyncStageMessage = () => {
    switch (syncStage) {
      case 'fetching':
        return 'Fetching your appointments...';
      case 'syncing':
        return syncProgress ? `Syncing appointments: ${syncProgress.synced} of ${syncProgress.total}` : 'Syncing appointments...';
      case 'completed':
        return 'Successfully synced all appointments!';
      case 'error':
        return 'An error occurred while syncing appointments.';
      default:
        return null;
    }
  };

  const renderSyncProgress = () => {
    if (!syncStage) return null;

    const progress = syncProgress?.total > 0 
      ? (syncProgress.synced / syncProgress.total) * 100 
      : 0;

    return (
      <div className="sync-progress">
        <div className="sync-progress-content">
          <div className="sync-progress-spinner" />
          <p className="sync-progress-text">
            {getSyncStageMessage()}
          </p>
        </div>
        {syncStage === 'syncing' && syncProgress?.total > 0 && (
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className="calendar-sync-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p className="loading-text">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="calendar-sync-page">
        <div className="alert alert--error">
          <div className="alert-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div className="alert-content">
            {error}
          </div>
          <div className="alert-actions">
            <button 
              className="alert-button alert-button--retry" 
              onClick={() => setError(null)}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!clientUser) {
    return (
      <div className="calendar-sync-page">
        <div className="alert alert--warning">
          <div className="alert-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div className="alert-content">
            Please log in to access calendar sync settings
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-sync-page">
      <div className="page-header">
        <a className="back-link" onClick={(e) => { e.preventDefault(); navigate(-1); }}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          Back to Home
        </a>
        <h1 className="page-title">Calendar Sync</h1>
      </div>

      <SyncOverlay stage={syncStage} progress={syncProgress} />

      <div className="calendar-providers">
        {calendarProviders.map(({ name, provider, icon, color, colorDark, description }) => (
          <div
            key={provider}
            className="calendar-provider-card"
            style={{
              '--provider-color': color,
              '--provider-color-dark': colorDark
            }}
          >
            <div className="calendar-provider-header">
              <div className="calendar-provider-info">
                <div className="calendar-provider-icon">
                  {icon}
                </div>
                <div className="calendar-provider-details">
                  <h3>{name}</h3>
                  <p>{description}</p>
                </div>
              </div>
              <div className="calendar-provider-actions">
                {syncStatus[provider].connected ? (
                  <button
                    className="sync-button sync-button--disconnect"
                    onClick={() => handleDisconnect(provider)}
                    disabled={isSyncing}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    Disconnect
                  </button>
                ) : (
                  <button
                    className="sync-button sync-button--connect"
                    onClick={() => handleSync(provider)}
                    disabled={isSyncing}
                  >
                    {icon}
                    {isSyncing ? 'Connecting...' : 'Connect'}
                  </button>
                )}
              </div>
            </div>
            {syncStatus[provider].error && (
              <div className="error-message">
                {syncStatus[provider].error}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarSync; 